<template>
	<div class="mt-3 rounded-lg bg-white shadow-sm ring-1 ring-gray-200 focus-within:ring-2 focus-within:ring-turquoise">
		<div class="flex gap-4 p-4 sm:p-5">
			<!-- Quem responde !-->
			<SupportTicketAvatar :label="authorLabel" variant="team" size="lg"/>
			<div class="min-w-0 flex-1">
				<!-- Cabeçalho, no mesmo formato das mensagens !-->
				<header class="flex items-center gap-2 border-b border-gray-100 pb-2">
					<span class="truncate text-sm font-semibold text-gray-900">{{ authorLabel }}</span>
					<span class="ml-auto flex-none truncate pl-2 text-xs text-gray-500">para {{ recipientList }}</span>
				</header>
				<!-- Campo !-->
				<div class="pt-2">
					<FormTextArea
						ref="body"
						:value="value"
						rows="7"
						placeholder="Escreva a resposta para este ticket"
						input-class="border-0 shadow-none focus:ring-0 px-0 leading-relaxed resize-y"
						@input="$emit('input', $event)"
						@keydown.native.meta.enter="handleSend"
						@keydown.native.ctrl.enter="handleSend"/>
				</div>
			</div>
		</div>
		<!-- Anexos escolhidos !-->
		<ul v-if="files.length" class="flex flex-wrap gap-2 border-t border-gray-100 px-4 py-3 sm:px-5">
			<li v-for="(item, index) in files" :key="`${item.name}-${item.size}`">
				<span class="inline-flex max-w-xs items-center gap-2 rounded-md bg-gray-50 px-3 py-1.5 text-xs text-gray-700 ring-1 ring-gray-200">
					<i class="fa fa-paperclip flex-none text-gray-400"></i>
					<span class="truncate" :title="item.name">{{ item.name }}</span>
					<span class="flex-none text-gray-400">{{ humanSize(item.size) }}</span>
					<button type="button" class="flex-none text-gray-400 hover:text-red-500" title="Remover anexo" @click="handleRemove(index)">
						<i class="fa fa-xmark"></i>
					</button>
				</span>
			</li>
		</ul>
		<!-- Ações !-->
		<div class="flex flex-wrap items-center justify-between gap-3 rounded-b-lg border-t border-gray-100 bg-gray-50 px-4 py-3 sm:px-5">
			<div class="flex items-center gap-3">
				<FormFile ref="fileField" name="file" multiple :value="file" @input="handleFiles">
					<span class="flex items-center gap-2">
						<i class="fa fa-paperclip text-gray-400"></i>
						Anexar arquivo
					</span>
				</FormFile>
				<span class="text-xs text-gray-500">{{ files.length }}/{{ maxFiles }}</span>
			</div>
			<div class="flex items-center gap-3">
				<span class="hidden text-xs text-gray-500 sm:inline">Ctrl + Enter envia</span>
				<button
					type="button"
					class="rounded-md px-3 py-2 text-sm font-semibold text-gray-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-300"
					@click="$emit('cancel')">
					Cancelar
				</button>
				<button
					type="button"
					:disabled="!canSend"
					class="inline-flex items-center gap-x-1.5 rounded-md bg-turquoise px-4 py-2 text-sm font-semibold text-white shadow-sm hover:brightness-95 focus:outline-none focus:ring-2 focus:ring-turquoise focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
					@click="handleSend">
					<i class="fa fa-paper-plane-top"></i>
					Enviar resposta
				</button>
			</div>
		</div>
	</div>
</template>

<script>
import FormTextArea from "@/components/FormTextArea.vue"
import FormFile from "@/components/FormFile.vue"

import SupportTicketAvatar from "./SupportTicketAvatar.vue"

export default {
	name: "SupportTicketComposer",
	components: {SupportTicketAvatar, FormTextArea, FormFile},
	props: {
		value: String,
		file: [Object, File, FileList, Array],
		/**
		 * Quantos arquivos a resposta aceita
		 */
		maxFiles: {
			type: Number,
			default: 5
		},
		authorLabel: {
			type: String,
			default: "Você"
		},
		/**
		 * Nomes de quem recebe a resposta
		 */
		recipients: {
			type: Array,
			default: () => []
		}
	},
	computed: {
		/**
		 * Anexos escolhidos, sempre como lista
		 */
		files() {
			return file_list(this.file)
		},
		canSend() {
			return !!this.value?.trim()
		},
		recipientList() {
			return this.recipients.length ? this.recipients.join(", ") : "todos os participantes"
		}
	},
	methods: {
		/**
		 * Junta a nova seleção com o que já estava anexado, sem repetir arquivo
		 * e respeitando o limite
		 * @param selection
		 */
		handleFiles(selection) {
			const chosen = Array.from(selection ?? [])

			if (!chosen.length) return

			const list = [...this.files]

			for (const file of chosen) {
				const repeated = list.some(item => item.name === file.name && item.size === file.size)
				if (!repeated) list.push(file)
			}

			// O campo é só o gatilho da escolha: a lista vive no componente
			this.$refs.fileField?.clear()

			if (list.length > this.maxFiles) {
				this.$alertError?.(`Você pode anexar no máximo ${this.maxFiles} arquivos.`)
			}

			this.$emit("update:file", list.slice(0, this.maxFiles))
		},
		/**
		 * @param index
		 */
		handleRemove(index) {
			const list = [...this.files]
			list.splice(index, 1)
			this.$emit("update:file", list)
		},
		/**
		 * @param size
		 * @returns {string}
		 */
		humanSize(size) {
			if (!size) return ""
			const units = ["B", "KB", "MB", "GB"]
			const index = Math.min(Math.floor(Math.log(size) / Math.log(1024)), units.length - 1)
			return `${(size / Math.pow(1024, index)).toFixed(index ? 1 : 0)} ${units[index]}`
		},
		handleSend() {
			if (!this.canSend) return
			this.$emit("send")
		},
		/**
		 * External
		 */
		focus() {
			this.$refs.body.focus()
		}
	}
}
</script>
